import React, { useEffect, useMemo } from 'react';
import './Slideshow.css';
import { useTranslation } from 'react-i18next';
import { useSchedule } from '../hooks/useSchedule';
import { useSlideshowTimer } from '../hooks/useSlideshowTimer';
import { useCurrentTime } from '../hooks/useCurrentTime';
import { getActiveSubPlanWithIndex } from '../utils/timeUtils';
import { getClientSessionId } from '../utils/tokenAuth';
import NoActivePlan from './NoActivePlan';
import DebugPanel from './DebugPanel';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// gallery-pipeline appends a trailing citation line to researched descriptions, e.g.
// "...本文...\n\n出典: ウィキペディア「東海道程ヶ谷」(CC BY-SA 4.0) https://ja.wikipedia.org/wiki/...".
// Splits that off so the citation label can be rendered as a link to the URL instead of
// showing the raw URL as text. Falls back to showing the description as-is if the
// citation line isn't found (e.g. older/manual entries without one).
function splitDescriptionCitation(description) {
  if (!description) return { bodyText: description, citationLabel: null, citationUrl: null };
  const match = description.match(/^([\s\S]*?)\n*(出典[:：].*?)\s*(https?:\/\/\S+)\s*$/);
  if (!match) return { bodyText: description, citationLabel: null, citationUrl: null };
  return { bodyText: match[1].trimEnd(), citationLabel: match[2].trim(), citationUrl: match[3] };
}

const Slideshow = ({ subscriptionId, scheduleId, scheduleName, email, onError, slideshowIntervalOverride, onSlideshowIntervalChange, debugMode, buttonsVisible, withDescription }) => {
  const { t } = useTranslation();
  const currentTime = useCurrentTime();
  const [imageError, setImageError] = React.useState(null);
  
  // Fetch schedule only when Slideshow component is mounted (user clicked "View in Browser")
  const { schedule, sessionEnded } = useSchedule(
    subscriptionId,
    scheduleId,
    email,
    true, // isSubmitted - always true when Slideshow is shown
    onError
  );

  // Update tab title to show schedule name
  useEffect(() => {
    document.title = scheduleName ? `CALMU GALLERY (${scheduleName})` : 'CALMU GALLERY';
    return () => { document.title = 'CALMU GALLERY'; };
  }, [scheduleName]);

  // End viewing session when component unmounts (user navigates away)
  useEffect(() => {
    const clientSessionId = getClientSessionId();
    
    // Also handle window/tab close events
    const handleBeforeUnload = () => {
      const payload = JSON.stringify({ subscriptionId, clientSessionId });
      const url = `${API_BASE}/api/viewing/end`;
      
      // sendBeacon is most reliable during page unload
      if (navigator.sendBeacon) {
        const blob = new Blob([payload], { type: 'application/json' });
        navigator.sendBeacon(url, blob);
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    // Cleanup function runs when component unmounts
    return () => {
      // Remove event listener
      window.removeEventListener('beforeunload', handleBeforeUnload);
      
      const payload = JSON.stringify({
        subscriptionId,
        clientSessionId
      });

      // Use sendBeacon for guaranteed delivery even when tab closes
      // Beacon API sends data asynchronously and survives page unload
      const url = `${API_BASE}/api/viewing/end`;
      
      if (navigator.sendBeacon) {
        // sendBeacon requires Blob for JSON content-type
        const blob = new Blob([payload], { type: 'application/json' });
        navigator.sendBeacon(url, blob);
      } else {
        // Browser doesn't support sendBeacon, use fetch with keepalive
        fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: payload,
          keepalive: true
        }).catch(error => console.error(`❌ [VIEWING] Fetch error:`, error));
      }
    };
  }, [subscriptionId]);

  const { plan: activePlan } = getActiveSubPlanWithIndex(schedule?.SubPlan || []);

  const activeImages = useMemo(() => (
    (activePlan?.images
      ? [...activePlan.images]
          .sort((a, b) => a.order - b.order)
          .map((imgRef) => schedule?.Images?.find((img) => img.image_id === imgRef.image_id))
          .filter(Boolean)
      : []) || []
  ), [activePlan?.images, schedule?.Images]);

  // Determine interval: user input > sub-schedule > default 60
  // useMemo ensures stable reference - only recalculates when dependencies change
  const slideshowInterval = useMemo(() => {
    if (slideshowIntervalOverride !== '' && slideshowIntervalOverride !== undefined) {
      const parsed = parseInt(slideshowIntervalOverride, 10);
      if (!isNaN(parsed) && parsed > 0) {
        return parsed;
      }
    }
    return activePlan?.duration_seconds || 60;
  }, [slideshowIntervalOverride, activePlan?.duration_seconds]);

  const { currentIndex, jumpToIndex } = useSlideshowTimer(slideshowInterval, activeImages.length);
  const currentImage = activeImages[currentIndex];

  const handleThumbnailClick = React.useCallback((imageId) => {
    const targetIndex = activeImages.findIndex((img) => img?.image_id === imageId);
    if (targetIndex >= 0) {
      jumpToIndex(targetIndex);
    }
  }, [activeImages, jumpToIndex]);

  // The 60 s heartbeat (useSchedule) reports a killed or expired session; there is no
  // separate session polling, to keep server load down.
  useEffect(() => {
    if (!sessionEnded) return;
    console.error('⛔ [SLIDESHOW] Session ended:', sessionEnded);
    setImageError(t(sessionEnded === 'SESSION_KILLED' ? 'app.sessionTerminatedMessage' : 'app.sessionExpiredMessage'));
  }, [sessionEnded, t]);

  // An image that fails to load means either the session was killed by an admin or the
  // collection was replaced by a weekly release (its old image packs are deleted).
  // The schedule is deliberately fetched only once per session, to keep server load
  // down, so instead of polling for new content the session ends here and the user
  // is asked to start it again, which fetches the new schedule.
  const imageErrorHandledRef = React.useRef(false);
  const handleImageError = async () => {
    if (imageErrorHandledRef.current) return;
    imageErrorHandledRef.current = true;
    let killed = false;
    try {
      // /api/session/check answers 200 with { error: 'SESSION_KILLED' } for a killed session
      const response = await fetch(`${API_BASE}/api/session/check?subscriptionId=${encodeURIComponent(subscriptionId)}`, { method: 'GET', credentials: 'include' });
      const body = await response.json().catch(() => ({}));
      killed = body.error === 'SESSION_KILLED';
    } catch (error) {
      console.error('❌ [SLIDESHOW] Failed to check session after image error:', error);
    }
    if (killed) {
      console.error('⛔ [SLIDESHOW] Image failed to load, session killed');
      setImageError(t('app.sessionTerminatedMessage'));
      return;
    }
    console.error('⛔ [SLIDESHOW] Image failed to load, ending the viewing session');
    fetch(`${API_BASE}/api/viewing/end`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ subscriptionId, clientSessionId: getClientSessionId() }),
      keepalive: true
    }).catch(error => console.error('❌ [VIEWING] Fetch error:', error));
    setImageError(t('app.contentUpdatedMessage'));
  };

  // Show loading state while fetching schedule
  if (!schedule) {
    return <div className="slideshow-container"><p>{t('app.loadingSchedule') || 'Loading schedule...'}</p></div>;
  }

  // Show no active plan message if no plan is available
  if (!activePlan || !currentImage) {
    return <NoActivePlan />;
  }

  // Backend transformed URLs: image_url is now removed, only image_id available
  // Use image_id to fetch through our authenticated proxy
  // URL encode image_id to handle special characters (e.g., colons)
  // CRITICAL: Include subscriptionId for schedule caching
  const imageSrc = (currentImage.image_id !== null && currentImage.image_id !== undefined)
    ? `${API_BASE}/api/images/${encodeURIComponent(currentImage.image_id)}?subscriptionId=${encodeURIComponent(subscriptionId)}` 
    : null;

  if (!imageSrc) {
    return null;
  }

  // Show error message if session was killed
  if (imageError) {
    return (
      <div className="slideshow-container">
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          padding: '20px',
          textAlign: 'center',
          color: '#d32f2f',
          backgroundColor: '#000'
        }}>
          <h2 style={{ marginBottom: '20px' }}>⛔ {t('app.sessionTerminatedTitle')}</h2>
          <p style={{ marginBottom: '20px', maxWidth: '600px' }}>{imageError}</p>
          <button 
            onClick={() => window.close()}
            style={{
              padding: '10px 20px',
              fontSize: '16px',
              backgroundColor: '#d32f2f',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            {t('app.closeTab')}
          </button>
        </div>
      </div>
    );
  }

  // Always show the description pane when "with description" viewing was requested,
  // regardless of whether this particular artwork/collection actually has data - the two
  // content sections below (per-artwork description, collection info) each show their own
  // "not available" fallback rather than the whole pane silently disappearing.
  const hasDescription = Boolean(withDescription);

  return (
    <div className={`slideshow-container${hasDescription ? ' with-description' : ''}`}>
      <div className="slideshow-image-pane">
        <img
          src={imageSrc}
          alt={t('slideshow.imageAlt', { id: currentImage.image_id })}
          className="slideshow-image"
          onContextMenu={(e) => e.preventDefault()}
          onError={handleImageError}
          draggable={false}
        />
      </div>
      {hasDescription && (
        <div className="slideshow-description-pane">
          {currentImage.title && <h2 className="description-title">{currentImage.title}</h2>}
          {currentImage.artist && <p className="description-artist">{currentImage.artist}</p>}
          <dl className="description-meta">
            {currentImage.medium && (
              <>
                <dt>技法・材質</dt>
                <dd>{currentImage.medium}</dd>
              </>
            )}
            {currentImage.dimensions && (
              <>
                <dt>寸法</dt>
                <dd>{currentImage.dimensions}</dd>
              </>
            )}
            {currentImage.objectDate && (
              <>
                <dt>制作年</dt>
                <dd>{currentImage.objectDate}</dd>
              </>
            )}
            {currentImage.museum && (
              <>
                <dt>所蔵</dt>
                <dd>{currentImage.museum}</dd>
              </>
            )}
            {currentImage.licenseLabel && (
              <>
                <dt>ライセンス</dt>
                <dd>
                  {currentImage.licenseUrl ? (
                    <a href={currentImage.licenseUrl} target="_blank" rel="noopener noreferrer">{currentImage.licenseLabel}</a>
                  ) : (
                    currentImage.licenseLabel
                  )}
                </dd>
              </>
            )}
          </dl>
          <h3 className="description-section-title">作品について</h3>
          {currentImage.description ? (() => {
            const { bodyText, citationLabel, citationUrl } = splitDescriptionCitation(currentImage.description);
            return (
              <>
                {bodyText && <p className="description-text">{bodyText}</p>}
                {citationUrl && (
                  <p className="description-citation">
                    <a href={citationUrl} target="_blank" rel="noopener noreferrer">{citationLabel}</a>
                  </p>
                )}
              </>
            );
          })() : (
            <p className="description-text description-unavailable">説明はありません</p>
          )}
          <div className="description-collection-info">
            <h3 className="description-section-title">コレクションについて</h3>
            {currentImage.collectionDescription ? (
              <p className="description-text">{currentImage.collectionDescription}</p>
            ) : (
              <p className="description-text description-unavailable">情報はありません</p>
            )}

            <h3 className="description-section-title">作家について</h3>
            {currentImage.painters && currentImage.painters.length > 0 ? (
              currentImage.painters.map((painter, i) => (
                <div key={i} className="description-painter">
                  {painter.name && <p className="description-painter-name">{painter.name}</p>}
                  {painter.profile && <p className="description-text">{painter.profile}</p>}
                  {painter.wikipediaUrl && (
                    <p className="description-citation">
                      <a href={painter.wikipediaUrl} target="_blank" rel="noopener noreferrer">出典: ウィキペディア{painter.name ? `「${painter.name}」` : ''}</a>
                    </p>
                  )}
                </div>
              ))
            ) : (
              <p className="description-text description-unavailable">情報はありません</p>
            )}
          </div>
        </div>
      )}
      {debugMode && buttonsVisible && (
        <DebugPanel
          currentTime={currentTime}
          subscriptionId={subscriptionId}

          schedule={schedule}
          activePlan={activePlan}
          lastFetchedAt={new Date()}
          slideshowInterval={slideshowIntervalOverride || slideshowInterval}
          onSlideshowIntervalChange={onSlideshowIntervalChange}
          onThumbnailClick={handleThumbnailClick}
        />
      )}
    </div>
  );
};

export default Slideshow;
